const PREFIX = `pin-sport-admin`;

type StorageType = 'local' | 'session';

class StorageSimulator {
  private storage: Record<string, any> = {};

  getItem(key: string) {
    if (this.storage[key] == null) {
      return null;
    }
    return this.storage[key];
  }

  setItem(key: string, items: any) {
    this.storage[key] = items;
  }

  removeItem(key: string) {
    delete this.storage[key];
  }

  key(index: number) {
    return Object.keys(this.storage)[index];
  }

  get length() {
    return Object.keys(this.storage).length;
  }
}

export function setItem<Item>(
  storageType: StorageType,
  key: string,
  value: Item
) {
  try {
    storage[storageType].setItem(`${PREFIX}-${key}`, JSON.stringify(value));
  } catch (error) {
    console.error('Error setting item in storage', error);
  }
}

export function removeItem(storageType: StorageType, key: string) {
  storage[storageType].removeItem(`${PREFIX}-${key}`);
}

export function getItem<Item>(
  storageType: StorageType,
  key: string,
  initialValue: Item
): Item;
export function getItem<Item>(
  storageType: StorageType,
  key: string
): Item | undefined;
export function getItem<Item>(
  storageType: StorageType,
  key: string,
  initialValue?: Item
) {
  const value = JSON.parse(storage[storageType].getItem(`${PREFIX}-${key}`));
  if (value == null) {
    if (initialValue !== undefined) {
      setItem(storageType, key, initialValue);
      return initialValue;
    }
    return undefined;
  }
  return value;
}

export function clearStorage(storageType: 'local' | 'session') {
  for (let i = 0; i < storage[storageType].length; i++) {
    const key = storage[storageType].key(i);
    if (key?.startsWith(PREFIX)) {
      storage[storageType].removeItem(key);
    }
  }
}

let localStorageSupported: boolean | null = null;
// When testing if storage is supported we can't just check if `getItem()` works,
// we need to actually attempt to write a value.
// See https://gist.github.com/paulirish/5558557
function isLocalStorageSupported() {
  if (localStorageSupported == null) {
    try {
      window.localStorage.setItem('isLocalStorageSupported', 'test');
      window.localStorage.removeItem('isLocalStorageSupported');
      localStorageSupported = true;
    } catch (_error) {
      localStorageSupported = false;
    }
  }

  return localStorageSupported;
}

let sessionStorageSupported: boolean | null = null;
// When testing if storage is supported we can't just check if `getItem()` works,
// we need to actually attempt to write a value.
// See https://gist.github.com/paulirish/5558557
function isSessionStorageSupported() {
  if (sessionStorageSupported == null) {
    try {
      window.sessionStorage.setItem('isSessionStorageSupported', 'test');
      window.sessionStorage.removeItem('isSessionStorageSupported');
      sessionStorageSupported = true;
    } catch (_error) {
      sessionStorageSupported = false;
    }
  }

  return sessionStorageSupported;
}

const storage: Record<StorageType, Storage | StorageSimulator> = {
  session: isSessionStorageSupported()
    ? window.sessionStorage
    : new StorageSimulator(),
  local: isLocalStorageSupported()
    ? window.localStorage
    : new StorageSimulator(),
};
